import React, { useEffect, useMemo, useCallback } from 'react';
import moment from 'moment';
import { Popover } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import '../../campaign/styles/CampaignModal.css';
import Calendar from '../../../assets/customSVG/customCalendar';
import SearchBar from '../../../components/commonComponents/searchBar';
import DropDown from '../../../components/commonComponents/DropDown/DropDown';
import CampaignFilter from '../../../components/commonComponents/ListViewHeader/CampaignFilter';
import { LeftList as calenderLeftList } from '../../../constant/app/calender/calender';
import { calendarHeader, calendarOption } from '../../../constant/app/campaign/campaign';
import { useAspSelector, useAspDispatch } from '../../../test/jest-redux-hooks';
import { updateComponentsSwitching } from '../../../reduxToolkit/CampaignSlice';

// Constants
const VIEW_OPTIONS = [
  { value: 'Week', label: 'Week', key: 'W' },
  { value: 'Month', label: 'Month', key: 'M' },
];

const BUTTON_CLASS = {
  active: 'bg-blue-600 text-white border-blue-600',
  inactive: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
};

const HeaderSwitchingButtons = ({
  handlePrevMonth,
  handleNextMonth,
  handlePrevWeek,
  handleNextWeek,
  selectedDate,
  shortMonthName,
  monthNamesArr,
  setWeekDates,
  setStartOfWeek,
  setEndOfWeek,
  startOfWeek,
  endOfWeek,
  pageNavigate,
  onFilterChange,
  selectedStatus,
  calendarSearch,
  searchisFilter,
  setSearchIsFilter,
  setPageNavigate,
  activeView,
  setActiveView,
}) => {
  const dispatch = useAspDispatch();
  const navigate = useNavigate();
  const { switchingComponents } = useAspSelector((state) => state.Campaign);

  // Update week dates when selected date changes
  useEffect(() => {
    const startOfWeekDate = moment(selectedDate).utc().startOf('week');
    const endOfWeekDate = moment(selectedDate).utc().endOf('week');

    const datesBetween = [];
    let currentDate = moment(startOfWeekDate).utc();

    while (currentDate <= endOfWeekDate) {
      datesBetween.push(moment(currentDate).utc());
      currentDate = moment(currentDate).utc().add(1, 'day');
    }

    setStartOfWeek(startOfWeekDate);
    setEndOfWeek(endOfWeekDate);
    setWeekDates(datesBetween);
  }, [selectedDate, setStartOfWeek, setEndOfWeek, setWeekDates]);

  // Handle campaign filter navigation
  const handleCampaignFilter = useCallback(
    (item) => {
      setPageNavigate(item?.name);
      navigate('/user/calendar');
    },
    [setPageNavigate, navigate]
  );

  // Handle view option selection
  const handleViewOptionClick = useCallback(
    (option) => {
      const selectedItem = calenderLeftList?.find(
        (item) => item.name === option.value
      );
      if (selectedItem) {
        handleCampaignFilter(selectedItem);
      }
    },
    [handleCampaignFilter]
  );

  // Handle component switching
  const handleComponentSwitch = useCallback(
    (componentName) => {
      dispatch(updateComponentsSwitching(componentName));
    },
    [dispatch]
  );

  // Memoized month/year display
  const monthYearDisplay = useMemo(() => {
    if (pageNavigate === 'Month' && selectedDate) {
      return `${shortMonthName[selectedDate?.getMonth()]?.slice(0, 9)} - ${selectedDate?.getFullYear()}`;
    }
    return '';
  }, [pageNavigate, selectedDate, shortMonthName]);

  // Memoized week range display
  const weekRangeDisplay = useMemo(() => {
    if (pageNavigate === 'Week' && startOfWeek && endOfWeek) {
      return `${moment(startOfWeek).utc().format('MMM')} ${moment(startOfWeek).utc().date()} - ${moment(endOfWeek).utc().format('MMM')} ${moment(endOfWeek).utc().date()}`;
    }
    return '';
  }, [pageNavigate, startOfWeek, endOfWeek]);

  return (
    <div id="stickyBar" className="sticky">
      <div className="w-full flex items-center justify-between h-[50px] border-b border-[--border-50] px-2">
        {/* Search Bar */}
        <div className="flex-row w-40 mt-10 mb-10">
          <SearchBar
            searchKey={searchisFilter}
            setSearchKey={setSearchIsFilter}
            onChange={calendarSearch}
            search="search-calender"
            searchCancel="CalendarsearchCancel"
            expandable={true}
          />
        </div>

        {/* Month View Header */}
        {activeView === 'calendar' && pageNavigate === 'Month' && switchingComponents !== 'Board' && selectedDate && monthNamesArr && (
          <div>
            {calendarHeader?.map((item) => (
              <div key={item?.id} className="calender-container flex-row align-center">
                {item?.image && (
                  <img
                    src={item?.image}
                    alt="calendar previous"
                    className="ml-5 mr-3 pointer !w-[1vw] !h-[1vw]"
                    onClick={handlePrevMonth}
                  />
                )}
                <Calendar width="18" height="18" color="var(--textBlack)" />
                <div className="monthWithYear pl-2">{monthYearDisplay}</div>
                {item?.icon1 && (
                  <img
                    src={item?.icon1}
                    alt="calendar next"
                    className="ml-4 mr-5 pointer !w-[1vw] !h-[1vw]"
                    onClick={handleNextMonth}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Week View Header */}
        {activeView === 'calendar' && pageNavigate === 'Week' && switchingComponents !== 'Board' && selectedDate && shortMonthName && (
          <div>
            {calendarHeader?.map((item) => (
              <div key={item?.id} className="calender-container flex-row align-center">
                {item?.image && (
                  <img
                    src={item?.image}
                    alt="calendar previous"
                    className="ml-5 mr-3 pointer !w-[1vw] !h-[1vw]"
                    onClick={handlePrevWeek}
                  />
                )}
                <Calendar width="18" height="18" color="var(--textBlack)" />
                <div className="monthWithYear pl-2">{weekRangeDisplay}</div>
                {item?.icon1 && (
                  <img
                    src={item?.icon1}
                    alt="calendar next"
                    className="ml-4 mr-5 pointer !w-[1vw] !h-[1vw]"
                    onClick={handleNextWeek}
                  />
                )}
              </div>
            ))}
          </div>
        )}

        {/* Toggle View Buttons */}
        {/* <div className="flex justify-center my-4">
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              onClick={() => setActiveView('calendar')}
              className={`px-6 py-2 text-sm font-medium rounded-l-lg border ${
                activeView === 'calendar' ? BUTTON_CLASS.active : BUTTON_CLASS.inactive
              }`}
            >
              Calendar
            </button>
            <button
              type="button"
              onClick={() => setActiveView('booking')}
              className={`px-6 py-2 text-sm font-medium rounded-r-lg border ${
                activeView === 'booking' ? BUTTON_CLASS.active : BUTTON_CLASS.inactive
              }`}
            >
              Booking
            </button>
          </div>
        </div> */}

        {/* Calendar Controls */}
        {activeView === 'calendar' && (
          <div className="flex-row align-center gap-x-2">
            {/* View Options Dropdown */}
            <div className="mr-5">
              <Popover
                placement="bottomLeft"
                trigger="click"
                content={
                  <div className="w-[220px] rounded-[12px] border border-[#E8E8EA] bg-white shadow-lg py-2">
                    {VIEW_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        onClick={() => handleViewOptionClick(option)}
                        className={`flex items-center justify-between px-4 py-2 text-left text-[14px] hover:bg-[#F1F6FF] cursor-pointer ${
                          pageNavigate === option.value
                            ? 'text-[var(--new-primary)] font-medium'
                            : 'text-[#1F1F24]'
                        }`}
                      >
                        <span>{option.label}</span>
                        <span className="text-xs text-[#9A9AA5] uppercase">
                          {option.key}
                        </span>
                      </div>
                    ))}
                  </div>
                }
              >
                <button
                  type="button"
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-[#E8E8EA] rounded-full text-[14px] font-medium shadow-sm hover:border-[#d0d0d4] transition-colors h-9 min-w-[120px] justify-between"
                >
                  {pageNavigate || 'Month'}
                  <DownOutlined className="text-xs text-[#6B6B76]" />
                </button>
              </Popover>
            </div>

            {/* Component Switcher & Filters */}
            <div className="w-40 flex-row flex-end">
              <div className="dropDownHover p-relative">
                <div className="dropdownWrapper pointer p-absolute">
                  {calendarOption?.map((item) => (
                    <div
                      key={item?.id}
                      onClick={() => handleComponentSwitch(item?.name)}
                      className="dropdown-1"
                    >
                      <DropDown title={item?.name} />
                    </div>
                  ))}
                </div>
              </div>
              <CampaignFilter
                onFilterChange={onFilterChange}
                selectedStatus={selectedStatus}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HeaderSwitchingButtons;